import { type AxiosPromise, isAxiosError } from "axios";
import { err, ok, type Result, ResultAsync } from "neverthrow";
import { RichErrors, type RichError } from "./errors";

export class UnhandledDiscriminatorError extends Error {
  constructor(
    public readonly discriminator: string,
    public readonly response: unknown
  ) {
    super(`Unhandled discriminator value: ${discriminator}`);
  }
}

/**
 * Wraps a `makeRequest` function that calls Axios. The function should return
 * a result type. `makeAxiosRequest` will automatically handle any Axios errors
 * and optionally use the provided `mapError` function to transform errors to a
 * custom `Failure` type.
 */
export const makeAxiosRequest = <
  Response extends Record<DiscriminatorKey, string>,
  DiscriminatorKey extends keyof Response,
  SuccessValue extends Response[DiscriminatorKey],
  FailureValue extends Response[DiscriminatorKey],
  Success = Response & { [k in DiscriminatorKey]: SuccessValue },
  Failure = Response & { [k in DiscriminatorKey]: FailureValue },
>(
  promiseOrMakeRequest: AxiosPromise<Response> | (() => AxiosPromise<Response>),
  keys: {
    discriminator: DiscriminatorKey;
    success: SuccessValue;
    failure: FailureValue;
  },
  map?: {
    success?: (
      response: Response & { [k in DiscriminatorKey]: SuccessValue }
    ) => Success;
    failure?: (
      response: Response & { [k in DiscriminatorKey]: FailureValue }
    ) => Failure;
    other?: (
      response: Response & {
        [k in DiscriminatorKey]: Exclude<
          Response[DiscriminatorKey],
          SuccessValue | FailureValue
        >;
      }
    ) => Result<Success, RichError<Failure>>;
  }
): ResultAsync<Success, RichError<Failure>> => {
  const mapSuccess = map?.success ?? ((response) => response as Success);
  const mapFailure = map?.failure ?? ((response) => response as Failure);
  const mapOther =
    map?.other ??
    ((response) =>
      err(
        RichErrors.unknown(
          new UnhandledDiscriminatorError(
            response[keys.discriminator],
            response
          )
        )
      ));

  return ResultAsync.fromPromise(
    typeof promiseOrMakeRequest === "function"
      ? promiseOrMakeRequest()
      : promiseOrMakeRequest,
    (error): RichError<Failure> => {
      if (isAxiosError(error)) {
        return RichErrors.axios(error);
      } else {
        return RichErrors.unknown(error);
      }
    }
  ).andThen(({ data }) => {
    switch (data[keys.discriminator]) {
      case keys.success:
        return ok(
          mapSuccess(
            data as Response & { [k in DiscriminatorKey]: SuccessValue }
          )
        );
      case keys.failure:
        return err(
          RichErrors.failure(
            mapFailure(
              data as Response & { [k in DiscriminatorKey]: FailureValue }
            )
          )
        );
      default:
        return mapOther(
          data as Response & {
            [k in DiscriminatorKey]: Exclude<
              Response[DiscriminatorKey],
              SuccessValue | FailureValue
            >;
          }
        );
    }
  });
};
